import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  CartesianGridProps,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { readChallengeDashboard } from '../api/endpoints';
import type {
  ChallengeDailyRow,
  ChallengeDashboard,
  ChallengeDefinition,
  ChallengeLevelRow,
} from '../api/types';
import { CHART_SERIES } from '../theme/palette';

import { ChartCard } from './ChartCard';
import { errorTypes as errorTypesPlural, levels as levelsPlural } from './slovakPlural';
import { ErrorNotice } from './ErrorNotice';
import { StatTile } from './StatTile';
import { useChartTheme } from './useChartTheme';

type ChallengeDashboardDialogProps = {
  challenge: ChallengeDefinition | null;
  onClose: () => void;
};

const WINDOWS = [7, 30, 90];

const percent = (value: number | null): string => (value === null ? '—' : `${Math.round(value * 100)} %`);

const seconds = (ms: number | null): string => (ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`);

/** `2026-08-26` reads as `26.8.` on an axis — the year is the same for every tick and costs width. */
const shortDay = (day: string): string => {
  const [, month, date] = day.split('-');

  return `${Number(date)}.${Number(month)}.`;
};

/**
 * One challenge, read closely.
 *
 * The catalogue could say what a challenge *is* and never what it has been doing. The two readings that
 * cannot be got anywhere else are the split by level — a challenge solved 90 % of the time at level 2 and
 * 20 % at level 3 has a cliff between them, and one accuracy figure hides it completely — and the check of
 * what the definition declares against what has actually happened.
 */
export const ChallengeDashboardDialog = ({ challenge, onClose }: ChallengeDashboardDialogProps) => {
  const [days, setDays] = useState(30);
  const chartTheme = useChartTheme();
  const { data, error, isLoading } = useQuery<ChallengeDashboard>({
    queryKey: ['challenge-dashboard', challenge?.code, days],
    queryFn: () => readChallengeDashboard(challenge?.code as string, days),
    enabled: challenge !== null,
  });

  const totals = data?.totals;
  const unservedLevels = data?.declarations.declaredLevels.filter((level) => !level.served) ?? [];
  const unusedErrorTypes = data?.declarations.declaredErrorTypes.filter((row) => row.occurrences === 0) ?? [];
  const slowerThanEstimated =
    totals !== undefined &&
    totals.averageResponseMs !== null &&
    totals.averageResponseMs > totals.estimatedDurationMs;

  return (
    <Dialog open={challenge !== null} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6">{challenge?.name}</Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontFamily: 'ui-monospace, monospace' }}
            >
              {challenge?.code}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, next: number | null) => {
              if (next !== null) {
                setDays(next);
              }
            }}
          >
            {WINDOWS.map((window) => (
              <ToggleButton key={window} value={window}>
                {window} dní
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <ErrorNotice error={error} />

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : null}

        {data && totals ? (
          totals.issued === 0 ? (
            <Alert severity="info">
              Táto úloha nebola za posledných {days} dní ani raz nasadená. Skúste dlhšie obdobie — alebo je
              vypnutá, prípadne ju skladba tréningu nedokáže zvoliť.
            </Alert>
          ) : (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <StatTile label="Nasadení" value={totals.issued} hint={`${totals.closed} uzavretých`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    label="Úspešnosť"
                    value={percent(totals.accuracy)}
                    hint={`${totals.solved} vyriešených`}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    label="Čas na odpoveď"
                    value={seconds(totals.averageResponseMs)}
                    hint={`odhad ${seconds(totals.estimatedDurationMs)}`}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile
                    label="Vypršalo"
                    value={percent(totals.expiryRate)}
                    hint={`${totals.averageAttempts ?? '—'} pokusov, ${totals.averageHints ?? '—'} nápovedy`}
                  />
                </Grid>
              </Grid>

              {slowerThanEstimated ? (
                <Alert severity="warning">
                  Deti odpovedajú v priemere {seconds(totals.averageResponseMs)}, hoci definícia počíta s{' '}
                  {seconds(totals.estimatedDurationMs)}. Spolu s {percent(totals.expiryRate)} vypršaných
                  zadaní to naznačuje, že časový limit je prikrátky.
                </Alert>
              ) : null}

              <ChartCard
                title="Podľa úrovne"
                description="Jedno číslo úspešnosti za celú úlohu nepovie nič — úloha sa zadáva na tej úrovni, na ktorú dieťa dosiahlo. Zlom medzi dvoma úrovňami je práve to, čo sa dá opraviť."
                height={224}
              >
                <BarChart
                  data={data.byLevel.map((row: ChallengeLevelRow) => ({
                    level: `úr. ${row.level}`,
                    accuracy: row.accuracy ?? 0,
                    issued: row.issued,
                  }))}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                >
                  <CartesianGrid {...(chartTheme.grid as CartesianGridProps)} />
                  <XAxis dataKey="level" {...chartTheme.axis} />
                  <YAxis
                    domain={[0, 1]}
                    tickFormatter={(value: number) => `${Math.round(value * 100)} %`}
                    {...chartTheme.axis}
                  />
                  <Tooltip
                    {...chartTheme.tooltip}
                    formatter={(value: number, name: string) =>
                      name === 'Úspešnosť' ? `${Math.round(value * 100)} %` : value
                    }
                  />
                  <Bar dataKey="accuracy" name="Úspešnosť" fill={CHART_SERIES[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Vývoj v čase"
                description="Či zmena zadania alebo obťažnosti zabrala."
                height={200}
              >
                <LineChart
                  data={data.daily.map((row: ChallengeDailyRow) => ({ ...row, accuracy: row.accuracy ?? 0 }))}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                >
                  <CartesianGrid {...(chartTheme.grid as CartesianGridProps)} />
                  <XAxis dataKey="day" tickFormatter={shortDay} {...chartTheme.axis} />
                  <YAxis
                    domain={[0, 1]}
                    tickFormatter={(value: number) => `${Math.round(value * 100)} %`}
                    {...chartTheme.axis}
                  />
                  <Tooltip
                    {...chartTheme.tooltip}
                    formatter={(value: number) => `${Math.round(value * 100)} %`}
                  />
                  <Line
                    type="monotone"
                    dataKey="accuracy"
                    name="Úspešnosť"
                    stroke={CHART_SERIES[2]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartCard>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Čo definícia sľubuje a čo sa naozaj deje
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Definícia úlohy je súbor sľubov — tieto úrovne, tieto chyby. Doteraz ich nič neporovnávalo
                  so skutočnosťou.
                </Typography>

                {data.declarations.undeclaredErrorTypes.length > 0 ? (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    Zaznamenali sa chyby, ktoré definícia vôbec nedeklaruje:{' '}
                    {data.declarations.undeclaredErrorTypes.map((row) => row.errorType).join(', ')}.
                    Klasifikátor a katalóg sa rozišli — v zázname dieťaťa je typ chyby, ktorý nič
                    nevysvetľuje.
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Paper variant="outlined" sx={{ p: 2, minWidth: 260, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Deklarované úrovne
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      {data.declarations.declaredLevels.map((level) => (
                        <Chip
                          key={level.level}
                          size="small"
                          label={level.level}
                          color={level.served ? 'success' : 'default'}
                          variant={level.served ? 'filled' : 'outlined'}
                          sx={{ minWidth: 38, opacity: level.served ? 1 : 0.5 }}
                        />
                      ))}
                    </Stack>
                    {unservedLevels.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {levelsPlural(unservedLevels.length)} z {data.declarations.declaredLevels.length} sa
                        za toto obdobie nezadalo. Buď na ne zatiaľ nikto nedosiahol, alebo ich skladba
                        tréningu nevie zvoliť.
                      </Typography>
                    ) : null}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, minWidth: 260, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Deklarované typy chýb
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {data.declarations.declaredErrorTypes.map((row) => (
                        <Stack key={row.errorType} direction="row" justifyContent="space-between" spacing={2}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'ui-monospace, monospace',
                              color: row.occurrences === 0 ? 'text.disabled' : 'text.primary',
                            }}
                          >
                            {row.errorType}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              color: row.occurrences === 0 ? 'text.disabled' : 'text.secondary',
                            }}
                          >
                            {row.occurrences === 0 ? 'nikdy' : row.occurrences}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    {unusedErrorTypes.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {errorTypesPlural(unusedErrorTypes.length)} nezaznamenalo ani raz — buď je tá vetva
                        klasifikátora nedosiahnuteľná, alebo nefunguje.
                      </Typography>
                    ) : null}
                  </Paper>
                </Stack>
              </Box>
            </Stack>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
